"use client";

import React from "react";
import { useDispatch } from "react-redux";
import { updateSection } from "@/store/slices/webbuilderSlice";
import { DynamicInput } from "../../ui/elements/DynamicInput";
import { ProductSelector } from "@/components/common/ProductSelector";
import { useFileManagerModal } from "@/components/fileManager";
import { FileSystemItem } from "@/types/fileManager";
import { generateImageSrc } from "@/utils/helper";
import { resolveSectionInputs } from "@team-shiphouse/shiphouse-themes/schema";
import { ChevronLeft, ChevronRight, Image as ImageIcon, Monitor, Plus, Smartphone, Tablet, Trash2, X } from "lucide-react";

type SchemaPanelProps = {
  schema: any;
  defaultInputs: any;
  selectedSection: any;
  currentDevice: "desktop" | "tablet" | "mobile";
  isLocked?: boolean;
};

type ViewportMode = "desktop" | "tablet" | "mobile";

const kindToInputType: Record<string, string> = {
  text: "text",
  default: "default",
  richText: "richtext",
  richtext: "richtext",
  image: "image",
  multiimage: "multiimage",
  video: "video",
  multivideo: "multivideo",
  link: "link",
  select: "select",
  checkbox: "checkbox",
  color: "color",
  number: "number",
  range: "range",
  date: "date",
  customcss: "customcss",
  customjs: "customjs",
  customhtml: "customhtml",
  customjson: "customjson",
  "text-list": "text-list",
  "link-list": "link-list",
  "image-list": "image-list",
  "icon-list": "icon-list",
  "feature-card-list": "feature-card-list",
  "testimonial-list": "testimonial-list",
  "faq-list": "faq-list",
  "accordion-list": "accordion-list",
  "tab-list": "tab-list",
  "media-gallery": "media-gallery",
  "product-selector": "product-selector",
  "product-filter": "product-filter",
  "category-selector": "category-selector",
  "category-filter": "category-filter",
  "collection-selector": "collection-selector",
  "product-metafield-binding": "product-metafield-binding",
  "collection-metafield-binding": "collection-metafield-binding",
  "section-style-config": "section-style-config",
  fontFace: "select",
  fontWeight: "select",
  "asset-selector": "asset-selector",
  "navigation-menu": "navigation-menu",
  "social-links-config": "social-links-config",
  "spacing-config": "spacing-config",
  "typography-config": "typography-config",
  "grid-config": "grid-config",
  "animation-config": "animation-config",
  "style-config": "style-config",
  "seo-config": "seo-config",
  productSource: "product-selector",
  cardImageStyle: "card-image-style-config",
  cardTitleStyle: "card-title-style-config",
  cardPriceStyle: "card-price-style-config",
  cardCtaStyle: "card-cta-style-config",
  cardStyle: "card-style-config",
  swiper: "swiper-config",
};

function clone<T>(value: T): T {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
}

function getPath(root: any, path: string[]) {
  return path.reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), root);
}

function setPath(root: any, path: string[], value: unknown) {
  const next = clone(root || {});
  let cursor = next;
  path.slice(0, -1).forEach((key) => {
    if (!cursor[key] || typeof cursor[key] !== "object") cursor[key] = {};
    cursor = cursor[key];
  });
  cursor[path[path.length - 1]] = value;
  return next;
}

function setObjectPath(root: any, path: string[], value: unknown) {
  const next = clone(root || {});
  let cursor = next;
  path.slice(0, -1).forEach((key) => {
    if (!cursor[key] || typeof cursor[key] !== "object") cursor[key] = {};
    cursor = cursor[key];
  });
  cursor[path[path.length - 1]] = value;
  return next;
}

function isResponsiveField(field: any) {
  return Boolean(field?.responsive || field?.default?.__responsive);
}

function getBucketForField(field: any, currentDevice: ViewportMode) {
  return isResponsiveField(field) ? currentDevice : "base";
}

function fieldValue(
  inputs: any,
  defaults: any,
  path: string[],
  bucket: ViewportMode | "base",
) {
  return getPath(inputs?.[bucket] || {}, path) ?? getPath(defaults?.[bucket] || {}, path) ?? getPath(defaults?.base || {}, path);
}

function isFieldVisible(field: any, inputs: any, defaults: any, currentDevice: ViewportMode) {
  if (!field?.visibleWhen?.path) return true;
  const path = String(field.visibleWhen.path).split(".");
  const value = fieldValue(inputs, defaults, path, currentDevice) ?? fieldValue(inputs, defaults, path, "base");

  if ("equals" in field.visibleWhen) return value === field.visibleWhen.equals;
  if ("notEquals" in field.visibleWhen) return value !== field.visibleWhen.notEquals;
  if ("exists" in field.visibleWhen) return field.visibleWhen.exists ? value !== undefined : value === undefined;
  return true;
}

function countVisibleFields(fields: Record<string, any>, inputs: any, defaults: any, currentDevice: "desktop" | "tablet" | "mobile") {
  return Object.values(fields || {}).filter((field) => isFieldVisible(field, inputs, defaults, currentDevice)).length;
}

const viewportOptions: Array<{ value: ViewportMode; label: string; Icon: typeof Monitor }> = [
  { value: "desktop", label: "Desktop", Icon: Monitor },
  { value: "tablet", label: "Tablet", Icon: Tablet },
  { value: "mobile", label: "Mobile", Icon: Smartphone },
];

function ResponsiveModeToggle({
  value,
  onChange,
}: {
  value: ViewportMode;
  onChange: (value: ViewportMode) => void;
}) {
  return (
    <div className="flex items-center rounded-md border border-border bg-background p-0.5">
      {viewportOptions.map(({ value: optionValue, label, Icon }) => (
        <button
          key={optionValue}
          type="button"
          title={label}
          aria-label={label}
          className={`rounded px-1.5 py-1 transition-colors ${
            value === optionValue ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
          onClick={(event) => {
            event.stopPropagation();
            onChange(optionValue);
          }}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

function summarizeFieldValue(value: unknown): string {
  if (value === undefined || value === null || value === "") return "Not set";
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? "" : "s"}`;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Enabled" : "Disabled";
  if (typeof value === "object") {
    const objectValue = value as Record<string, any>;
    if (objectValue.mode) return `${objectValue.enabled === false ? "Disabled" : "Enabled"} · ${objectValue.mode}`;
    if (objectValue.href) return objectValue.href;
    if (objectValue.asset_id) return "Image selected";
    if ("value" in objectValue && "unit" in objectValue) return `${objectValue.value}${objectValue.unit}`;
    return "Configured";
  }
  return "Configured";
}

function getGroupFieldsets(groupConfig: any) {
  const fields = groupConfig?.fields || {};
  return (groupConfig?.fieldsets || [])
    .map((fieldset: any) => ({
      ...fieldset,
      fields: (fieldset.fields || []).filter((fieldKey: string) => fields[fieldKey]),
    }))
    .filter((fieldset: any) => fieldset.fields.length > 0);
}

function SizeInput({ value, onChange }: { value: any; onChange: (value: any) => void }) {
  const current = value && typeof value === "object" ? value : { value: 0, unit: "px" };
  return (
    <div className="flex gap-2">
      <input
        type="number"
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        value={current.value ?? 0}
        onChange={(event) => onChange({ ...current, value: Number(event.target.value) })}
      />
      <select
        className="w-24 rounded-md border border-border bg-background px-2 py-2 text-sm"
        value={current.unit || "px"}
        onChange={(event) => onChange({ ...current, unit: event.target.value })}
      >
        {(["px", "rem", "em", "%", "vh", "vw"] as const).map((unit) => (
          <option key={unit} value={unit}>{unit}</option>
        ))}
      </select>
    </div>
  );
}

function defaultFieldValue(field: any): unknown {
  if (!field) return undefined;
  if (field.kind === "object" && field.fields) return defaultItemValue(field.fields);
  if (field.kind === "repeater") return [];
  if (field.default?.__responsive) return clone(field.default.desktop);
  return clone(field.default);
}

function defaultItemValue(fields: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(fields || {}).map(([key, field]) => [key, defaultFieldValue(field)]),
  );
}

function AssetInput({ value, onChange }: { value: any; onChange: (value: any) => void }) {
  const { openFileManager } = useFileManagerModal();
  const asset = value && typeof value === "object" ? value : null;
  const imageSrc = asset?.url || (asset?.path ? generateImageSrc(asset.path, 300, 200) : "");

  const handleSelect = (files: FileSystemItem[]) => {
    const file = files[0];
    if (!file) return;
    onChange({
      asset_id: file.assetId,
      url: file.url || "",
      alt: asset?.alt || file.name || "",
      width: asset?.width,
      height: asset?.height,
    });
  };

  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      {imageSrc ? (
        <div className="relative overflow-hidden rounded-lg border border-border bg-muted">
          <img src={imageSrc} alt={asset?.alt || "Selected image"} className="h-36 w-full object-cover" />
          <button
            type="button"
            className="absolute right-2 top-2 rounded-full bg-background/90 p-1 shadow"
            onClick={() => onChange(null)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : asset?.asset_id ? (
        <div className="rounded-lg border border-border bg-muted p-3 text-xs text-muted-foreground">
          Selected asset: {asset.asset_id}
        </div>
      ) : null}

      <button
        type="button"
        className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
        onClick={() =>
          openFileManager({
            selectionMode: "single",
            allowedFileTypes: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/svg+xml"],
            maxSelections: 1,
            showUpload: true,
            showCreateFolder: true,
            onSelect: handleSelect,
          })
        }
      >
        <ImageIcon className="h-4 w-4" />
        {asset ? "Change image" : "Select image"}
      </button>

      <input
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        value={asset?.alt || ""}
        onChange={(event) => onChange({ ...(asset || { asset_id: "", url: "" }), alt: event.target.value || undefined })}
        placeholder="Alt text"
      />
    </div>
  );
}

function LinkInput({ value, onChange }: { value: any; onChange: (value: any) => void }) {
  const current = value && typeof value === "object" ? value : { type: "url", href: "#" };
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <select
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        value={current.type || "url"}
        onChange={(event) => onChange({ ...current, type: event.target.value })}
      >
        <option value="url">URL</option>
        <option value="route">Route</option>
        <option value="product">Product</option>
        <option value="collection">Collection</option>
      </select>
      <input
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        value={current.href || ""}
        onChange={(event) => onChange({ ...current, href: event.target.value })}
        placeholder="https://example.com or #"
      />
      <input
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        value={current.label || ""}
        onChange={(event) => onChange({ ...current, label: event.target.value || undefined })}
        placeholder="Optional label"
      />
      <select
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        value={current.target || "_self"}
        onChange={(event) => onChange({ ...current, target: event.target.value })}
      >
        <option value="_self">Same tab</option>
        <option value="_blank">New tab</option>
      </select>
    </div>
  );
}

function ProductSourceInput({ value, onChange }: { value: any; onChange: (value: any) => void }) {
  const current = value && typeof value === "object"
    ? value
    : { enabled: true, mode: "filter", filter: { limit: 4, sort_by: "created_at", sort_order: "desc" } };
  const filter = current.filter || {};

  const updateFilter = (patch: Record<string, unknown>) => {
    onChange({
      ...current,
      mode: "filter",
      filter: {
        ...filter,
        ...patch,
      },
    });
  };

  return (
    <div className="space-y-4 rounded-lg border border-border p-3">
      <label className="flex items-center justify-between gap-3 text-sm">
        <span>Use dynamic products</span>
        <input
          type="checkbox"
          checked={current.enabled !== false}
          onChange={(event) => onChange({ ...current, enabled: event.target.checked })}
        />
      </label>

      {current.enabled === false ? (
        <p className="text-xs text-muted-foreground">Static cards below will be used instead.</p>
      ) : null}

      <label className="space-y-1 block">
        <span className="text-xs font-medium text-muted-foreground">Mode</span>
        <select
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={current.mode || "filter"}
          onChange={(event) => {
            const mode = event.target.value;
            onChange(
              mode === "manual"
                ? { enabled: current.enabled, mode: "manual", identifiers: current.identifiers || [] }
                : { enabled: current.enabled, mode: "filter", filter: current.filter || {} },
            );
          }}
        >
          <option value="filter">Filter products</option>
          <option value="manual">Manual products</option>
        </select>
      </label>

      {current.mode === "manual" ? (
        <ProductSelector
          value={Array.isArray(current.identifiers) ? current.identifiers : []}
          onValueChange={(identifiers) => onChange({ ...current, mode: "manual", identifiers })}
          maxSelections={12}
        />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Limit</span>
              <input
                type="number"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={filter.limit ?? ""}
                onChange={(event) => updateFilter({ limit: event.target.value === "" ? undefined : Number(event.target.value) })}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Page</span>
              <input
                type="number"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={filter.page ?? ""}
                onChange={(event) => updateFilter({ page: event.target.value === "" ? undefined : Number(event.target.value) })}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Sort by</span>
              <select
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={filter.sort_by || "created_at"}
                onChange={(event) => updateFilter({ sort_by: event.target.value })}
              >
                <option value="created_at">Created</option>
                <option value="updated_at">Updated</option>
                <option value="title">Title</option>
                <option value="status">Status</option>
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Order</span>
              <select
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={filter.sort_order || "desc"}
                onChange={(event) => updateFilter({ sort_order: event.target.value })}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </label>
          </div>

          <label className="space-y-1 block">
            <span className="text-xs font-medium text-muted-foreground">Title contains</span>
            <input
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={filter.title || ""}
              onChange={(event) => updateFilter({ title: event.target.value || undefined })}
            />
          </label>

          <label className="space-y-1 block">
            <span className="text-xs font-medium text-muted-foreground">Tag</span>
            <input
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={filter.tag || ""}
              onChange={(event) => updateFilter({ tag: event.target.value || undefined })}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Min price</span>
              <input
                type="number"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={filter.min_price ?? ""}
                onChange={(event) => updateFilter({ min_price: event.target.value === "" ? undefined : Number(event.target.value) })}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Max price</span>
              <input
                type="number"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={filter.max_price ?? ""}
                onChange={(event) => updateFilter({ max_price: event.target.value === "" ? undefined : Number(event.target.value) })}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export function SchemaSectionPropertiesPanel({
  schema,
  defaultInputs,
  selectedSection,
  currentDevice,
  isLocked = false,
}: SchemaPanelProps) {
  const dispatch = useDispatch();
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [activeGroupKey, setActiveGroupKey] = React.useState<string | null>(null);
  const [activeFieldCategoryKey, setActiveFieldCategoryKey] = React.useState<string | null>(null);
  const [activeFieldKey, setActiveFieldKey] = React.useState<string | null>(null);
  const [activeRepeaterPath, setActiveRepeaterPath] = React.useState<string[] | null>(null);
  const [activeRepeaterItemIndex, setActiveRepeaterItemIndex] = React.useState<number | null>(null);
  const [fieldViewports, setFieldViewports] = React.useState<Record<string, ViewportMode>>({});
  const sectionInputs = selectedSection?.section_inputs || defaultInputs;
  const groups = Object.entries(schema.groups || {}) as Array<[string, any]>;
  const activeGroup = activeGroupKey ? schema.groups?.[activeGroupKey] : null;

  React.useEffect(() => {
    setActiveGroupKey(null);
    setActiveFieldCategoryKey(null);
    setActiveFieldKey(null);
    setActiveRepeaterPath(null);
    setActiveRepeaterItemIndex(null);
    setFieldViewports({});
  }, [selectedSection?.id, schema?.version]);

  const pathKey = (path: string[]) => path.join(".");
  const getFieldViewport = (field: any, path: string[]): ViewportMode => {
    if (!isResponsiveField(field)) return currentDevice;
    return fieldViewports[pathKey(path)] || currentDevice;
  };
  const setFieldViewport = (path: string[], viewport: ViewportMode) => {
    setFieldViewports((current) => ({ ...current, [pathKey(path)]: viewport }));
  };

  const updateValue = (field: any, path: string[], value: unknown, viewport = getFieldViewport(field, path)) => {
    if (isLocked || !selectedSection) return;
    const bucket = getBucketForField(field, viewport);
    const nextInputs = {
      ...clone(sectionInputs),
      schemaIdentifier: selectedSection.section_identifier,
      schemaVersion: schema.version,
      [bucket]: setPath(sectionInputs?.[bucket] || {}, path, value),
    };

    try {
      resolveSectionInputs({
        schema,
        sectionIdentifier: selectedSection.section_identifier,
        value: nextInputs,
        viewport,
      });
      setValidationError(null);
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : String(error));
      return;
    }

    dispatch(updateSection({ ...selectedSection, section_inputs: nextInputs }));
  };

  const renderControl = (field: any, value: any, onChange: (value: unknown) => void) => {
    if (field.kind === "fontSize" || field.kind === "spacing") {
      return <SizeInput value={value} onChange={onChange} />;
    }

    if (field.kind === "image") {
      return <AssetInput value={value} onChange={onChange} />;
    }

    if (field.kind === "link") {
      return <LinkInput value={value} onChange={onChange} />;
    }

    if (field.kind === "productSource") {
      return <ProductSourceInput value={value} onChange={onChange} />;
    }

    return (
      <DynamicInput
        config={{
          title: field.label,
          description: field.description,
          input_key: field.label,
          type: (kindToInputType[field.kind] || "customjson") as any,
          accepted_values: field.options as any,
        }}
        value={value}
        onChange={onChange}
        hideLabel
      />
    );
  };

  const renderRepeater = (field: any, path: string[], value: any[], hideHeader = false) => {
    const items = Array.isArray(value) ? value : [];
    const minItems = Number.isFinite(field.minItems) ? field.minItems : 0;
    const maxItems = Number.isFinite(field.maxItems) ? field.maxItems : undefined;
    const isActiveRepeater = activeRepeaterPath?.join(".") === path.join(".");
    const editingItemIndex = isActiveRepeater ? activeRepeaterItemIndex : null;

    const updateItems = (nextItems: any[]) => updateValue(field, path, nextItems);

    if (editingItemIndex !== null && editingItemIndex !== undefined && items[editingItemIndex]) {
      const item = items[editingItemIndex];
      return (
        <div key={path.join(".")} className="space-y-4">
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            onClick={() => {
              setActiveRepeaterPath(null);
              setActiveRepeaterItemIndex(null);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            Back to {field.label}
          </button>

          <div className="border-t border-border pt-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-base font-semibold">{item?.title || `Card ${editingItemIndex + 1}`}</div>
                <div className="text-xs text-muted-foreground">Edit one static card.</div>
              </div>
              <button
                type="button"
                className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                disabled={items.length <= minItems}
                onClick={() => {
                  updateItems(items.filter((_, itemIndex) => itemIndex !== editingItemIndex));
                  setActiveRepeaterPath(null);
                  setActiveRepeaterItemIndex(null);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              {Object.entries(field.item || {}).map(([itemKey, itemField]: [string, any]) => {
                const itemValue = item?.[itemKey] ?? defaultFieldValue(itemField);
                return (
                  <div key={itemKey} className="space-y-2">
                    <label className="text-sm font-medium">{itemField.label}</label>
                    {renderControl(itemField, itemValue, (nextValue) => {
                      const nextItems = [...items];
                      nextItems[editingItemIndex] = setObjectPath(item || {}, [itemKey], nextValue);
                      updateItems(nextItems);
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={path.join(".")} className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between gap-3">
          {!hideHeader ? (
            <div>
              <div className="text-sm font-semibold">{field.label}</div>
              {field.description ? <p className="text-xs text-muted-foreground">{field.description}</p> : null}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">{items.length} card{items.length === 1 ? "" : "s"}</div>
          )}
          <button
            type="button"
            className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
            disabled={Boolean(maxItems && items.length >= maxItems)}
            onClick={() => updateItems([...items, defaultItemValue(field.item || {})])}
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        </div>

        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
            No cards yet. Add a card to show static content.
          </div>
        ) : null}

        <div className="space-y-2">
          {items.map((item, index) => (
            <button
              key={index}
              type="button"
              className="flex w-full items-center justify-between gap-3 border-b border-border px-1 py-3 text-left transition-colors hover:bg-muted/50"
              onClick={() => {
                setActiveRepeaterPath(path);
                setActiveRepeaterItemIndex(index);
              }}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold">{item?.title || `Card ${index + 1}`}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {[item?.price, item?.ctaText, item?.href?.href].filter(Boolean).join(" · ") || "Open to edit card details"}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderField = (field: any, path: string[], options: { hideHeader?: boolean } = {}) => {
    if (!isFieldVisible(field, sectionInputs, defaultInputs, currentDevice)) {
      return null;
    }

    if (field.kind === "object" && field.fields) {
      return (
        <div key={path.join(".")} className="border-t border-border pt-3">
          {!options.hideHeader ? <div className="mb-3 text-sm font-semibold">{field.label}</div> : null}
          <div className="space-y-4">
            {Object.entries(field.fields).map(([key, child]) => renderField(child, [...path, key]))}
          </div>
        </div>
      );
    }

    const fieldViewport = getFieldViewport(field, path);
    const bucket = getBucketForField(field, fieldViewport);
    const value = fieldValue(sectionInputs, defaultInputs, path, bucket);

    if (field.kind === "repeater") {
      return renderRepeater(field, path, value || [], Boolean(options.hideHeader));
    }

    return (
      <div key={path.join(".")} className="space-y-2">
        {!options.hideHeader ? (
          <div>
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm font-medium">{field.label}</label>
              {isResponsiveField(field) ? (
                <ResponsiveModeToggle value={fieldViewport} onChange={(viewport) => setFieldViewport(path, viewport)} />
              ) : null}
            </div>
            {field.description ? <p className="text-xs text-muted-foreground">{field.description}</p> : null}
          </div>
        ) : null}
        {renderControl(field, value, (next) => updateValue(field, path, next, fieldViewport))}
      </div>
    );
  };

  const renderGroupList = () => (
    <div className="divide-y divide-border border-y border-border">
      {groups.map(([groupKey, groupConfig]) => {
        const visibleCount = countVisibleFields(groupConfig.fields || {}, sectionInputs, defaultInputs, currentDevice);
        return (
          <button
            key={groupKey}
            type="button"
            className="flex w-full items-center justify-between gap-3 px-1 py-3 text-left transition-colors hover:bg-muted/50"
            onClick={() => {
      setActiveGroupKey(groupKey);
      setActiveFieldCategoryKey(null);
      setActiveFieldKey(null);
              setActiveRepeaterPath(null);
              setActiveRepeaterItemIndex(null);
            }}
          >
            <div className="min-w-0">
              <div className="font-semibold">{groupConfig.label}</div>
              <div className="text-xs text-muted-foreground">
                {groupConfig.description || `${visibleCount} setting${visibleCount === 1 ? "" : "s"}`}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        );
      })}
    </div>
  );

  const renderActiveGroup = () => {
    if (!activeGroupKey || !activeGroup) return null;
    const fields = Object.entries(activeGroup.fields || {}) as Array<[string, any]>;
    const activeField = activeFieldKey ? activeGroup.fields?.[activeFieldKey] : null;

    const fieldsets = getGroupFieldsets(activeGroup);

    if (fieldsets.length > 0 && !activeFieldKey) {
      const activeCategory = fieldsets.find((category: any) => category.key === activeFieldCategoryKey);
      const nestedCategories = fieldsets.filter((category: any) => category.layout === "nested");
      const inlineCategories = fieldsets.filter((category: any) => category.layout !== "nested");

      if (!activeCategory) {
        return (
          <div className="space-y-4">
            <button
              type="button"
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => {
                setActiveGroupKey(null);
                setActiveFieldCategoryKey(null);
                setActiveFieldKey(null);
                setActiveRepeaterPath(null);
                setActiveRepeaterItemIndex(null);
              }}
            >
              <ChevronLeft className="h-4 w-4" />
              Back to groups
            </button>

            <div className="border-b border-border pb-3">
              <div className="text-base font-semibold">{activeGroup.label}</div>
              {activeGroup.description ? <p className="text-xs text-muted-foreground">{activeGroup.description}</p> : null}
            </div>

            <div className="divide-y divide-border border-y border-border">
              {nestedCategories.map((category: any) => (
                <button
                  key={category.key}
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-1 py-3 text-left transition-colors hover:bg-muted/50"
                  onClick={() => setActiveFieldCategoryKey(category.key)}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{category.label}</div>
                    <div className="truncate text-xs text-muted-foreground">{category.description}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
              {inlineCategories.map((category: any) => {
                const visibleFields = category.fields.filter((fieldKey: string) => {
                  const field = activeGroup.fields?.[fieldKey];
                  return field && isFieldVisible(field, sectionInputs, defaultInputs, currentDevice);
                });
                if (visibleFields.length === 0) return null;
                return (
                  <div key={category.key} className="py-4">
                    {visibleFields.map((fieldKey: string) => renderField(activeGroup.fields[fieldKey], [activeGroupKey, fieldKey]))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            onClick={() => {
              setActiveFieldCategoryKey(null);
              setActiveFieldKey(null);
              setActiveRepeaterPath(null);
              setActiveRepeaterItemIndex(null);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
              Back to {activeGroup.label}
          </button>

          <div className="border-b border-border pb-3">
            <div className="text-base font-semibold">{activeCategory.label}</div>
            <p className="text-xs text-muted-foreground">{activeCategory.description}</p>
          </div>

          <div className="divide-y divide-border border-y border-border">
            {activeCategory.fields.map((fieldKey: string) => {
              const field = activeGroup.fields?.[fieldKey];
              if (!field || !isFieldVisible(field, sectionInputs, defaultInputs, currentDevice)) return null;
              if (field.layout?.presentation === "nested") {
                const fieldPath = [activeGroupKey, fieldKey];
                const fieldViewport = getFieldViewport(field, fieldPath);
                const bucket = getBucketForField(field, fieldViewport);
                const value = fieldValue(sectionInputs, defaultInputs, fieldPath, bucket);
                return (
                  <div
                    key={fieldKey}
                    className="flex w-full items-center justify-between gap-3 px-1 py-3 text-left transition-colors hover:bg-muted/50"
                  >
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setActiveFieldKey(fieldKey)}>
                      <div className="text-sm font-semibold">{field.label}</div>
                      <div className="truncate text-xs text-muted-foreground">{summarizeFieldValue(value)}</div>
                    </button>
                    <button type="button" className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => setActiveFieldKey(fieldKey)}>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                );
              }
              return (
                <div key={fieldKey} className="py-4">
                  {renderField(field, [activeGroupKey, fieldKey])}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (activeFieldKey && activeField) {
      return (
        <div className="space-y-4">
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            onClick={() => {
              setActiveFieldKey(null);
              setActiveRepeaterPath(null);
              setActiveRepeaterItemIndex(null);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            Back to {activeGroup.label}
          </button>

          {renderField(activeField, [activeGroupKey, activeFieldKey])}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <button
          type="button"
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          onClick={() => {
            setActiveGroupKey(null);
            setActiveFieldCategoryKey(null);
            setActiveFieldKey(null);
            setActiveRepeaterPath(null);
            setActiveRepeaterItemIndex(null);
          }}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to groups
        </button>

        <div className="border-b border-border pb-3">
          <div className="text-base font-semibold">{activeGroup.label}</div>
          {activeGroup.description ? <p className="text-xs text-muted-foreground">{activeGroup.description}</p> : null}
        </div>

        <div className="divide-y divide-border border-y border-border">
          {fields.map(([fieldKey, field]) => {
            if (!isFieldVisible(field, sectionInputs, defaultInputs, currentDevice)) return null;
            return (
              <div key={fieldKey} className="py-4">
                {renderField(field, [activeGroupKey, fieldKey])}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`h-full overflow-y-auto p-4 ${isLocked ? "pointer-events-none opacity-60" : ""}`}>
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Section Properties</h2>
        <p className="text-xs text-muted-foreground">Schema-driven inputs</p>
      </div>
      {validationError ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {validationError}
        </div>
      ) : null}
      {activeGroupKey ? renderActiveGroup() : renderGroupList()}
    </div>
  );
}
